import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Accordion from '../components/Accordion';
import BuyerInbox from '../system-modules/favorites/components/BuyerInbox';

// Rich Text Editor Toolbar - Updated
function EditorToolbar({ editor, onImageUpload, onYouTubeEmbed, onVimeoEmbed, uploadingImage }) {
  if (!editor) return null;
  
  return (
    <div className="border rounded-t-lg p-2 bg-gray-50 flex flex-wrap gap-1">
      <button onClick={() => editor.chain().focus().toggleBold().run()} className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-300' : ''}`} title="Bold"><strong>B</strong></button>
      <button onClick={() => editor.chain().focus().toggleItalic().run()} className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-300' : ''}`} title="Italic"><em>I</em></button>
      <button onClick={() => editor.chain().focus().toggleStrike().run()} className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('strike') ? 'bg-gray-300' : ''}`} title="Strike"><s>S</s></button>
      <span className="w-px bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-300' : ''}`} title="Heading 1">H1</button>
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''}`} title="Heading 2">H2</button>
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-300' : ''}`} title="Heading 3">H3</button>
      <span className="w-px bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-300' : ''}`} title="Bullet List">• List</button>
      <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-300' : ''}`} title="Numbered List">1. List</button>
      <span className="w-px bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().toggleBlockquote().run()} className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('blockquote') ? 'bg-gray-300' : ''}`} title="Quote">" Quote</button>
      <span className="w-px bg-gray-300 mx-1" />
      <button onClick={() => { const url = prompt('Enter URL:'); if (url) editor.chain().focus().setLink({ href: url }).run(); }} className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-300' : ''}`} title="Link">🔗</button>
      <button onClick={() => editor.chain().focus().unsetLink().run()} className="p-1.5 rounded hover:bg-gray-200" title="Remove Link">🔗✕</button>
      <span className="w-px bg-gray-300 mx-1" />
      <label className={`p-1.5 rounded hover:bg-gray-200 cursor-pointer ${uploadingImage ? 'opacity-50' : ''}`} title="Upload Image">
        🖼️
        <input type="file" accept="image/*" onChange={onImageUpload} className="hidden" disabled={uploadingImage} />
        {uploadingImage && <span className="text-xs ml-1">Uploading...</span>}
      </label>
      <button onClick={() => { const url = prompt('Enter image URL:'); if (url && editor) { editor.chain().focus().setImage({ src: url }).run(); } }} className="p-1.5 rounded hover:bg-gray-200" title="Insert Image URL">🖼️ URL</button>
      <span className="w-px bg-gray-300 mx-1" />
      <button onClick={onYouTubeEmbed} className="p-1.5 rounded hover:bg-gray-200" title="Embed YouTube Video">▶️ YouTube</button>
      <button onClick={onVimeoEmbed} className="p-1.5 rounded hover:bg-gray-200" title="Embed Vimeo Video">🎬 Vimeo</button>
    </div>
  );
}

function RichTextEditor({ content, onChange, placeholder = 'Write your content here...' }) {
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const editor = useEditor({
    extensions: [StarterKit, Image, Link.configure({ openOnClick: false }), Placeholder.configure({ placeholder })],
    content: content || '',
    onUpdate: ({ editor }) => { onChange(editor.getHTML()); },
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      const response = await fetch('/api/upload/education', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.url || data.imageUrl;
        if (editor && imageUrl) {
          editor.chain().focus().setImage({ src: imageUrl }).run();
          toast.success('Image uploaded successfully!');
        }
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleYouTubeEmbed = () => {
    const url = prompt('Enter YouTube URL:');
    if (!url || !editor) return;
    const patterns = [/(?:youtube\.com\/watch\?v=)([^&]+)/, /(?:youtu\.be\/)([^?]+)/, /(?:youtube\.com\/embed\/)([^?]+)/];
    let videoId = null;
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) { videoId = match[1]; break; }
    }
    if (!videoId) { toast.error('Invalid YouTube URL'); return; }
    const embedHtml = `<div class="video-embed"><iframe width="100%" height="400" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe></div>`;
    editor.chain().focus().insertContent(embedHtml).run();
    toast.success('Video embedded successfully!');
  };

  const handleVimeoEmbed = () => {
    const url = prompt('Enter Vimeo URL:');
    if (!url || !editor) return;
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (!match) { toast.error('Invalid Vimeo URL'); return; }
    const videoId = match[1];
    const embedHtml = `<div class="video-embed"><iframe width="100%" height="400" src="https://player.vimeo.com/video/${videoId}" frameborder="0" allowfullscreen></iframe></div>`;
    editor.chain().focus().insertContent(embedHtml).run();
    toast.success('Video embedded successfully!');
  };

  return (
    <div className="border rounded-lg" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <EditorToolbar 
        editor={editor} 
        onImageUpload={handleImageUpload}
        onYouTubeEmbed={handleYouTubeEmbed}
        onVimeoEmbed={handleVimeoEmbed}
        uploadingImage={uploadingImage}
      />
      <EditorContent editor={editor} className="prose max-w-none p-4 min-h-[150px] focus:outline-none" />
    </div>
  );
}

export default function EducatorDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('content');
  const [modules, setModules] = useState([]);
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewModule, setPreviewModule] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'educator_content',
    targetRole: 'buyer',
    creditReward: 5,
    screens: [{ title: '', content: '', type: 'text' }]
  });

  // Fetch unread count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch('/api/favorites/communication/unread/count', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };
    fetchUnreadCount();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [modulesRes, statsRes, partnersRes] = await Promise.all([
        fetch('/api/educator/modules', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/educator/stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/educator/partners', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (modulesRes.ok) setModules(await modulesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (partnersRes.ok) setPartners(await partnersRes.json());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModule = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/educator/modules', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const newModule = await response.json();
        toast.success('Module created successfully!');
        setModules([newModule, ...modules]);
        setShowCreateModal(false);
        resetForm();
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create module');
      }
    } catch (error) {
      console.error('Error creating module:', error);
      toast.error('Failed to create module');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateModule = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/educator/modules/${editingModule._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const updatedModule = await response.json();
        toast.success('Module updated successfully!');
        setModules(modules.map(m => m._id === updatedModule._id ? updatedModule : m));
        setShowEditModal(false);
        resetForm();
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update module');
      }
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error('Failed to update module');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setFormData({
      title: module.title || '',
      description: module.description || '',
      type: module.type || 'educator_content',
      targetRole: module.targetRole || 'buyer',
      creditReward: module.creditReward || 5,
      screens: module.screens || [{ title: '', content: '', type: 'text' }]
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'educator_content',
      targetRole: 'buyer',
      creditReward: 5,
      screens: [{ title: '', content: '', type: 'text' }]
    });
    setEditingModule(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleScreenChange = (index, field, value) => {
    const updatedScreens = [...formData.screens];
    updatedScreens[index] = { ...updatedScreens[index], [field]: value };
    setFormData(prev => ({ ...prev, screens: updatedScreens }));
  };

  const handleScreenContentChange = (index, content) => {
    const updatedScreens = [...formData.screens];
    updatedScreens[index] = { ...updatedScreens[index], content };
    setFormData(prev => ({ ...prev, screens: updatedScreens }));
  };

  const addScreen = () => {
    setFormData(prev => ({
      ...prev,
      screens: [...prev.screens, { title: '', content: '', type: 'text' }]
    }));
  };

  const removeScreen = (index) => {
    if (formData.screens.length <= 1) {
      toast.error('Module must have at least one screen');
      return;
    }
    setFormData(prev => ({
      ...prev,
      screens: prev.screens.filter((_, i) => i !== index)
    }));
  };

  const handlePreview = (module) => {
    setPreviewModule(module);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const accordionItems = [
    {
      icon: '📚',
      title: 'Content',
      badge: modules.filter(m => !m.draft).length,
      content: (
        <div>
          {modules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-4">📝</div>
              <p>You haven't created any content yet.</p>
              <button onClick={() => setShowCreateModal(true)} className="mt-2 text-green-600 hover:text-green-700">
                Create your first module →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {modules.map(module => (
                <div key={module._id} className={`border rounded-lg p-4 ${module.draft ? 'bg-gray-50' : 'bg-white'}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{module.title}</h4>
                        {module.draft && <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">Draft</span>}
                        {module.featured && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">⭐ Featured</span>}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">{module.type}</span>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">{module.completions || 0} completions</span>
                        {module.creditReward > 0 && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">💰 {module.creditReward} credits</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button onClick={() => handleEditModule(module)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                      <button onClick={() => handlePreview(module)} className="text-green-600 hover:text-green-800 text-sm">Preview</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      icon: '🤝',
      title: 'Vendors',
      badge: partners.filter(p => p.status === 'active').length,
      content: (
        <div>
          {partners.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-4">🤝</div>
              <p>You haven't partnered with any vendors yet.</p>
              <p className="text-sm mt-1">Browse producers and hubs to start promoting their products.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {partners.map(partner => {
                const statusColors = {
                  pending: 'bg-yellow-100 text-yellow-800',
                  active: 'bg-green-100 text-green-800',
                  inactive: 'bg-gray-100 text-gray-800',
                  rejected: 'bg-red-100 text-red-800'
                };
                return (
                  <div key={partner.vendorId._id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold">{partner.vendorId.farmName || partner.vendorId.name}</h4>
                        <p className="text-sm text-gray-600">{partner.type}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${statusColors[partner.status]}`}>{partner.status}</span>
                          <span className="text-xs text-gray-500">Commission: {partner.commissionRate}%</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button className="text-gray-600 hover:text-gray-800 text-sm">View</button>
                        <button className="text-blue-600 hover:text-blue-800 text-sm">Products</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )
    },
    {
      icon: '📊',
      title: 'Analytics',
      badge: modules.length,
      content: (
        <div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="border rounded-lg p-3 text-center">
              <div className="text-sm text-gray-500">Students</div>
              <div className="text-xl font-bold">{stats?.totalStudents || 0}</div>
            </div>
            <div className="border rounded-lg p-3 text-center">
              <div className="text-sm text-gray-500">Completions</div>
              <div className="text-xl font-bold">{modules.reduce((sum, m) => sum + (m.completions || 0), 0)}</div>
            </div>
            <div className="border rounded-lg p-3 text-center">
              <div className="text-sm text-gray-500">Vendors</div>
              <div className="text-xl font-bold">{partners.filter(p => p.status === 'active').length}</div>
            </div>
            <div className="border rounded-lg p-3 text-center">
              <div className="text-sm text-gray-500">Commission</div>
              <div className="text-xl font-bold text-green-600">${stats?.commissionEarned || 0}</div>
            </div>
          </div>
          <div className="text-center text-sm text-gray-500">
            {modules.sort((a, b) => (b.completions || 0) - (a.completions || 0))[0]?.title && (
              <p>Most popular: <strong>{modules.sort((a, b) => (b.completions || 0) - (a.completions || 0))[0].title}</strong></p>
            )}
          </div>
        </div>
      )
    },
    {
      icon: '⚙️',
      title: 'Settings',
      content: (
        <div className="space-y-4">
          <h3 className="font-semibold">Profile Settings</h3>
          <div>
            <label className="block text-sm font-medium mb-1">Bio</label>
            <textarea
              value={user?.educatorSettings?.bio || ''}
              onChange={() => {}}
              rows={4}
              className="w-full border rounded-lg p-2"
              placeholder="Tell people about yourself and your expertise..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Specialties</label>
            <select
              multiple
              className="w-full border rounded-lg p-2"
              value={user?.educatorSettings?.specialties || []}
            >
              <option value="nutrition">Nutrition</option>
              <option value="recipe_development">Recipe Development</option>
              <option value="farming">Farming</option>
              <option value="sustainability">Sustainability</option>
              <option value="cooking">Cooking</option>
              <option value="food_science">Food Science</option>
              <option value="seasonal">Seasonal Eating</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
          </div>
          <button className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700">Save Settings</button>
        </div>
      )
    },
    {
      icon: '📬',
      title: 'Inbox',
      badge: unreadCount > 0 ? `${unreadCount} new` : 'Messages',
      content: <BuyerInbox />
    }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">🎓 Educator Dashboard</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
        >
          + New Content
        </button>
      </div>
      
      <Accordion 
        items={accordionItems} 
        defaultOpen={0}
        className="mt-4"
        headerClassName="text-base font-medium py-4"
        iconOpen="▼"
        iconClosed="▶"
      />

      {/* Create Module Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create New Content Module</h2>
            <form onSubmit={handleCreateModule} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" name="title" value={formData.title} onChange={handleFormChange} className="w-full border rounded-lg p-2" placeholder="Module title..." required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea name="description" value={formData.description} onChange={handleFormChange} className="w-full border rounded-lg p-2" rows="3" placeholder="Brief description..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select name="type" value={formData.type} onChange={handleFormChange} className="w-full border rounded-lg p-2">
                    <option value="educator_content">General Education</option>
                    <option value="recipe">Recipe</option>
                    <option value="nutrition">Nutrition</option>
                    <option value="seasonal">Seasonal</option>
                    <option value="sustainability">Sustainability</option>
                    <option value="farming_tips">Farming Tips</option>
                    <option value="cooking_skills">Cooking Skills</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                  <select name="targetRole" value={formData.targetRole} onChange={handleFormChange} className="w-full border rounded-lg p-2">
                    <option value="buyer">Buyers</option>
                    <option value="kin_member">Kin Members</option>
                    <option value="kin_coordinator">Kin Coordinators</option>
                    <option value="producer">Producers</option>
                    <option value="hub">Hubs</option>
                    <option value="pickup">Pickups</option>
                    <option value="educator">Educators</option>
                    <option value="all">Everyone</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Reward</label>
                <input type="number" name="creditReward" value={formData.creditReward} onChange={handleFormChange} className="w-full border rounded-lg p-2" min="0" step="1" />
                <p className="text-xs text-gray-500 mt-1">Credits users will earn upon completing this module</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Screens</label>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {formData.screens.map((screen, index) => (
                    <div key={index} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm font-medium">Screen {index + 1}</span>
                        <button type="button" onClick={() => removeScreen(index)} className="text-red-500 hover:text-red-700 text-sm">Remove</button>
                      </div>
                      <div className="space-y-3">
                        <input type="text" value={screen.title} onChange={(e) => handleScreenChange(index, 'title', e.target.value)} className="w-full border rounded p-2 text-sm" placeholder="Screen title" />
                        <select value={screen.type} onChange={(e) => handleScreenChange(index, 'type', e.target.value)} className="w-full border rounded p-2 text-sm">
                          <option value="text">Text</option>
                          <option value="quiz">Quiz</option>
                        </select>
                        {screen.type === 'quiz' ? (
                          <div className="space-y-3">
                            <input type="text" value={screen.quizQuestion || ''} onChange={(e) => handleScreenChange(index, 'quizQuestion', e.target.value)} className="w-full border rounded p-2 text-sm" placeholder="Quiz question" />
                            <div className="space-y-2">
                              {[0, 1, 2, 3].map(i => (
                                <input key={i} type="text" value={screen.quizOptions?.[i] || ''} onChange={(e) => { const options = [...(screen.quizOptions || ['', '', '', ''])]; options[i] = e.target.value; handleScreenChange(index, 'quizOptions', options); }} className="w-full border rounded p-2 text-sm" placeholder={`Option ${i + 1}`} />
                              ))}
                            </div>
                            <input type="number" value={screen.quizCorrectAnswer !== undefined ? screen.quizCorrectAnswer : ''} onChange={(e) => handleScreenChange(index, 'quizCorrectAnswer', parseInt(e.target.value))} className="w-full border rounded p-2 text-sm" placeholder="Correct answer (0-3)" min="0" max="3" />
                            <textarea value={screen.quizExplanation || ''} onChange={(e) => handleScreenChange(index, 'quizExplanation', e.target.value)} className="w-full border rounded p-2 text-sm" rows="2" placeholder="Explanation for correct answer" />
                          </div>
                        ) : (
                          <RichTextEditor content={screen.content} onChange={(content) => handleScreenContentChange(index, content)} placeholder="Write your content here..." />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addScreen} className="mt-2 text-sm text-green-600 hover:text-green-700">+ Add Screen</button>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                  {isSubmitting ? 'Creating...' : 'Create Module'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
